import { useState, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { pageLinks } from '../../pageLinks'
import { AppShell } from '../../components/AppShell'
import { getListings, removeListing, formatAddedAt, type TrackedListing } from './aplusListingsStore'
import {
  getAllMarketplaces,
  getEnabledMarketplaces,
  saveMarketplacePreferences,
  type MarketplacePreference,
} from './aplusMarketplaceStore'

// ============================================================================
// Components
// ============================================================================


function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${checked ? 'bg-[#18181B]' : 'bg-[#E5E5E5]'}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 size-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : ''}`}
      />
    </button>
  )
}

function DragHandle() {
  return (
    <svg className="size-4 text-[#A1A1AA] cursor-grab" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 9h16.5m-16.5 6.75h16.5" />
    </svg>
  )
}

// ============================================================================
// Settings Panel
// ============================================================================

function SettingsPanel({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [marketplaces, setMarketplaces] = useState<MarketplacePreference[]>(getAllMarketplaces)
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [overIdx, setOverIdx] = useState<number | null>(null)

  const toggleMarketplace = (code: string) => {
    setMarketplaces((prev) =>
      prev.map((m) => (m.code === code ? { ...m, enabled: !m.enabled } : m))
    )
  }

  const handleDragStart = (idx: number) => {
    setDragIdx(idx)
  }

  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault()
    setOverIdx(idx)
  }

  const handleDrop = (idx: number) => {
    if (dragIdx === null || dragIdx === idx) {
      setDragIdx(null)
      setOverIdx(null)
      return
    }
    setMarketplaces((prev) => {
      const next = [...prev]
      const [moved] = next.splice(dragIdx, 1)
      next.splice(idx, 0, moved)
      return next
    })
    setDragIdx(null)
    setOverIdx(null)
  }

  const handleDragEnd = () => {
    setDragIdx(null)
    setOverIdx(null)
  }

  const handleSave = () => {
    saveMarketplacePreferences(marketplaces)
    onSaved()
    onClose()
  }

  const enabledCount = marketplaces.filter((m) => m.enabled).length

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/20" />
      <div
        className="relative w-[420px] h-full bg-white border-l border-[#E5E5E5] shadow-lg overflow-y-auto animate-slide-in-right"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-[#E5E5E5] px-5 py-4 flex items-center justify-between">
          <h2 className="text-[14px] font-semibold text-[#18181B]">Settings</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#F5F5F5] transition-colors cursor-pointer">
            <svg className="size-5 text-[#71717A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Marketplace Preferences */}
          <div className="bg-white rounded-lg border border-[#E5E5E5] p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-[13px] font-semibold text-[#18181B]">Marketplace Preferences</h3>
                <p className="text-[12px] text-[#71717A] mt-0.5">Drag to reorder. Toggle to enable/disable.</p>
              </div>
              <span className="text-[12px] text-[#A1A1AA]">{enabledCount}/{marketplaces.length}</span>
            </div>
            <div>
              {marketplaces.map((mp, idx) => (
                <div
                  key={mp.code}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDrop={() => handleDrop(idx)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    idx < marketplaces.length - 1 ? 'border-b border-[#F0F0F0]' : ''
                  } ${dragIdx === idx ? 'opacity-40' : ''} ${overIdx === idx && dragIdx !== idx ? 'bg-[#EFF6FF] border-b-[#3B82F6]' : 'hover:bg-[#FAFAFA]'}`}
                >
                  <DragHandle />
                  <span className="text-sm">{mp.flag}</span>
                  <span className="text-[13px] text-[#18181B] flex-1">{mp.name}</span>
                  <span className="text-[11px] text-[#A1A1AA] mr-2">{mp.code}</span>
                  <Toggle checked={mp.enabled} onChange={() => toggleMarketplace(mp.code)} />
                </div>
              ))}
            </div>
          </div>

          {/* Save */}
          <div className="flex justify-end pt-2">
            <button
              onClick={handleSave}
              className="px-5 h-9 bg-[#18181B] text-white rounded-lg text-[13px] font-medium hover:bg-[#3F3F46] transition-colors cursor-pointer"
            >
              Save Settings
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.2s ease-out;
        }
      `}</style>
    </div>
  )
}

// ============================================================================
// Views
// ============================================================================

function MatrixView({ listings, search, marketplaces, onRemove }: { listings: TrackedListing[]; search: string; marketplaces: MarketplacePreference[]; onRemove: (asin: string) => void }) {
  const filtered = listings.filter((l) =>
    l.asin.toLowerCase().includes(search.toLowerCase()) ||
    l.title.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="bg-white rounded-lg border border-[#E5E5E5] overflow-hidden">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[#E5E5E5] bg-[#FAFAFA]">
            <th className="text-left text-[11px] font-medium text-[#71717A] uppercase tracking-wider px-4 py-3">ASIN</th>
            <th className="text-left text-[11px] font-medium text-[#71717A] uppercase tracking-wider px-4 py-3">Title</th>
            {marketplaces.map((m) => (
              <th key={m.code} className="text-center px-1 py-3">
                <div className="flex flex-col items-center gap-0.5">
                  <span className="text-xl leading-none">{m.flag}</span>
                  <span className="text-[10px] font-medium text-[#A1A1AA]">{m.code}</span>
                </div>
              </th>
            ))}
            <th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {filtered.map((listing, idx) => (
            <tr key={listing.asin} className={`group ${idx < filtered.length - 1 ? 'border-b border-[#F0F0F0]' : ''} hover:bg-[#FAFAFA] transition-colors`}>
              <td className="px-4 py-3">
                <Link to={pageLinks.AplusListingDetail(listing.asin)} className="text-[13px] font-medium text-[#3B82F6] hover:underline">
                  {listing.asin}
                </Link>
              </td>
              <td className="px-4 py-3">
                <p className="text-[13px] text-[#52525B]">{listing.title}</p>
              </td>
              {marketplaces.map((m) => (
                <td key={m.code} className="px-1 py-2">
                  <div className="flex justify-center">
                    <a
                      href={`https://www.${m.domain}/dp/${listing.asin}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg hover:bg-[#F0F0F0] transition-colors"
                      title={`Open on ${m.domain}`}
                    >
                      <svg className="size-4 text-[#A1A1AA] hover:text-[#3B82F6]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                      </svg>
                    </a>
                  </div>
                </td>
              ))}
              <td className="px-1 py-2">
                <button
                  onClick={() => onRemove(listing.asin)}
                  className="p-2 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-[#FEF2F2] transition-all cursor-pointer"
                  title="Remove listing"
                >
                  <svg className="size-4 text-[#A1A1AA] hover:text-[#EF4444]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                  </svg>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {filtered.length === 0 && (
        <div className="py-12 text-center">
          <p className="text-[13px] text-[#A1A1AA]">No listings found matching &ldquo;{search}&rdquo;</p>
        </div>
      )}
    </div>
  )
}

function CardView({ listings, search, marketplaces, onRemove }: { listings: TrackedListing[]; search: string; marketplaces: MarketplacePreference[]; onRemove: (asin: string) => void }) {
  const filtered = listings.filter((l) =>
    l.asin.toLowerCase().includes(search.toLowerCase()) ||
    l.title.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="grid grid-cols-2 gap-4">
      {filtered.map((listing) => (
        <div
          key={listing.asin}
          className="group bg-white rounded-lg border border-[#E5E5E5] p-4 relative"
        >
          <div className="mb-3">
            <div className="flex items-center justify-between">
              <Link to={pageLinks.AplusListingDetail(listing.asin)} className="hover:underline">
                <p className="text-[13px] font-semibold text-[#3B82F6]">{listing.asin}</p>
              </Link>
              <button
                onClick={() => onRemove(listing.asin)}
                className="p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-[#FEF2F2] transition-all cursor-pointer -mr-1"
                title="Remove listing"
              >
                <svg className="size-3.5 text-[#A1A1AA] hover:text-[#EF4444]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
                </svg>
              </button>
            </div>
            <p className="text-[12px] text-[#71717A] mt-0.5 truncate">{listing.title}</p>
            <span className="text-[11px] text-[#A1A1AA]">{formatAddedAt(listing.addedAt)}</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {marketplaces.map((m) => (
              <a
                key={m.code}
                href={`https://www.${m.domain}/dp/${listing.asin}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-[#F0F0F0] transition-colors"
                title={`Open on ${m.domain}`}
              >
                <span className="text-xl leading-none">{m.flag}</span>
                <svg className="size-3.5 text-[#A1A1AA]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                </svg>
              </a>
            ))}
          </div>
        </div>
      ))}
      {filtered.length === 0 && (
        <div className="col-span-2 py-12 text-center">
          <p className="text-[13px] text-[#A1A1AA]">No listings found matching &ldquo;{search}&rdquo;</p>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Page
// ============================================================================

export default function AplusListings() {
  const [view, setView] = useState<'matrix' | 'card'>('matrix')
  const [search, setSearch] = useState('')
  const [showSettings, setShowSettings] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)
  const listings = useMemo(() => getListings(), [refreshKey])
  // Read enabled marketplaces (respects saved order + visibility)
  // Re-read when refreshKey changes (after settings are saved or listing removed)
  const enabledMarketplaces = useMemo(() => getEnabledMarketplaces(), [refreshKey])
  const handleSettingsSaved = useCallback(() => {
    setRefreshKey((k) => k + 1)
  }, [])
  const handleRemove = useCallback((asin: string) => {
    removeListing(asin)
    setRefreshKey((k) => k + 1)
  }, [])

  return (
    <AppShell activeNav="listings">
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-lg font-semibold text-[#18181B]">Listings</h1>
              <p className="text-[13px] text-[#71717A] mt-1">Review A+ content page across markets</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowSettings(true)}
                className="p-2 border border-[#E5E5E5] rounded-lg hover:bg-[#FAFAFA] transition-colors cursor-pointer"
                title="Settings"
              >
                <svg className="size-4 text-[#71717A]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28Z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                </svg>
              </button>
              <Link
                to={pageLinks.AplusAddListing()}
                className="px-4 h-9 bg-[#18181B] text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-[#3F3F46] transition-colors"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add Listing
              </Link>
            </div>
          </div>

          {/* Toolbar */}
          <div className="flex items-center justify-between mb-4">
            <div className="relative">
              <svg className="size-4 text-[#A1A1AA] absolute left-3 top-1/2 -translate-y-1/2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by ASIN or title..."
                className="pl-9 pr-4 h-9 border border-[#E5E5E5] rounded-lg text-[13px] w-72 outline-none focus:border-[#3B82F6] transition-colors placeholder:text-[#A1A1AA]"
              />
            </div>
            <div className="flex items-center border border-[#E5E5E5] rounded-lg overflow-hidden">
              <button
                onClick={() => setView('matrix')}
                className={`px-3 h-8 text-[12px] font-medium transition-colors cursor-pointer ${view === 'matrix' ? 'bg-[#18181B] text-white' : 'bg-white text-[#71717A] hover:bg-[#F5F5F5]'}`}
              >
                Matrix
              </button>
              <button
                onClick={() => setView('card')}
                className={`px-3 h-8 text-[12px] font-medium transition-colors cursor-pointer ${view === 'card' ? 'bg-[#18181B] text-white' : 'bg-white text-[#71717A] hover:bg-[#F5F5F5]'}`}
              >
                Cards
              </button>
            </div>
          </div>

          {/* Content */}
          {listings.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="size-12 rounded-full bg-[#F5F5F5] flex items-center justify-center mb-4">
                <svg className="size-6 text-[#A1A1AA]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0ZM3.75 12h.007v.008H3.75V12Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm-.375 5.25h.007v.008H3.75v-.008Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
                </svg>
              </div>
              <h2 className="text-[15px] font-semibold text-[#18181B] mb-1">No listings yet</h2>
              <p className="text-[13px] text-[#71717A] mb-4 text-center max-w-sm">Add an ASIN to start reviewing A+ content pages across your enabled marketplaces.</p>
              <Link
                to={pageLinks.AplusAddListing()}
                className="px-4 h-9 bg-[#18181B] text-white rounded-lg text-sm font-medium flex items-center gap-2 hover:bg-[#3F3F46] transition-colors"
              >
                <svg className="size-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                Add Listing
              </Link>
            </div>
          ) : view === 'matrix' ? (
            <MatrixView listings={listings} search={search} marketplaces={enabledMarketplaces} onRemove={handleRemove} />
          ) : (
            <CardView listings={listings} search={search} marketplaces={enabledMarketplaces} onRemove={handleRemove} />
          )}
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && <SettingsPanel onClose={() => setShowSettings(false)} onSaved={handleSettingsSaved} />}
    </AppShell>
  )
}
